import {
  Tierart,
  Umstellungsstatus,
  WarenkorbProduktError,
  WarenkorbValidationResult,
} from '@ps20/bff-models';
import {
  App,
  areAppTypesEquivalent,
  BasisRisikoort,
  getEquivalentApps,
  isEqualAdresse,
  isMultistoreApp,
  StandaloneProdukt,
  TierartEnum,
  TkvV2Vorbelegungdaten,
  TkvVorbelegungdaten,
  Vertrag,
  VhvVorbelegungdaten,
  Vorbelegungdaten,
  Vorvertrag,
  VorvertragAdresse,
  VwgVorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import { RsAndRsVermietung } from '@ps20-frontend/shop-integration-redux';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { WarenkorbAngebot } from '../reducers/produkte.reducer';
import { IAppState } from '../appState';
import { NeugeschaeftSperren } from '../model/neugeschaeftSperren';
import {
  VorvertragDataForModal,
  VorvertragForModal,
} from '../../app/vertraege-ersetzen-auswahl-modal/vertraege-ersetzen-auswahl-modal.component';
import { getPrintedVpDaten, VP_PREFIX } from '../sagas/vorvertragsUebersichtSaga';
import {
  selectIsNeugeschaeftSperrenDeactivated,
  selectTarifierungIsInProgress,
  standaloneModalOpenForVertrag,
} from './transient-state.selector';
import {
  selectAssekuranztarifChanged,
  selectAssekuranztarifMixed,
} from './vorvertrag-hinweis.selector';

//ToDo: eine Function, der alle Kombinationen aus Vertraegen, Fremdvertraegen und weiterenVertraegen zurueckgibt
export const selectVorvertraege = (
  state: IAppState,
  involveWeitereVertraege?: boolean,
): Array<Vorvertrag> => {
  if (state.vorvertraege) {
    return involveWeitereVertraege
      ? state.vorvertraege.concat(selectOnlyWeitereVertraege(state))
      : state.vorvertraege;
  }
  return [];
};
export const selectOnlyWeitereVertraege = (state: IAppState): Array<Vorvertrag> => {
  return state.weitereVertraege ? state.weitereVertraege : [];
};
export const selectSelectedWeiterenVertrag = (
  state: IAppState,
  vorvertragToCheck: Vorvertrag,
  checkAlle?: boolean,
): Vorvertrag => {
  if (checkAlle) {
    return selectFromVorvertragToCheck(selectAlleVorvertraege(state, true), vorvertragToCheck);
  }
  return selectFromVorvertragToCheck(selectOnlyWeitereVertraege(state), vorvertragToCheck);
};

export const selectFromVorvertragToCheck = (
  input: Array<Vorvertrag>,
  vorvertragToCheck: Vorvertrag,
): Vorvertrag => {
  return input.find(
    weitererVertrag =>
      weitererVertrag.vertragsnummer === vorvertragToCheck.vertragsnummer &&
      isEqual(weitererVertrag.produktIds, vorvertragToCheck.produktIds) &&
      !vorvertragToCheck.spLaufnummer &&
      vorvertragToCheck.produktIds?.length === 1,
  );
};

export const selectStandaloneProdukte = (state: IAppState): Array<StandaloneProdukt> =>
  state.standaloneProdukte ? state.standaloneProdukte : [];
export const selectFremdVertraege = (state: IAppState): Array<Vorvertrag | StandaloneProdukt> =>
  state.fremdVertraege;
export const selectWeitereVertraege = (state: IAppState): Array<Vorvertrag | StandaloneProdukt> => {
  const weitereVertraege: Array<Vorvertrag | StandaloneProdukt> = [];
  weitereVertraege.push(...state.fremdVertraege, ...selectOnlyWeitereVertraege(state));
  return weitereVertraege;
};
export const selectAlleVorvertraege = (
  state: IAppState,
  involveWeitereVertraege: boolean = true,
): Array<Vorvertrag> =>
  selectFremdStandardProdukte(state)?.concat(selectVorvertraege(state, involveWeitereVertraege));

export const selectAlleStandaloneProdukte = (state: IAppState): Array<StandaloneProdukt> =>
  selectFremdStandaloneProdukte(state).concat(selectStandaloneProdukte(state));

export const selectTierartFromVorvertrag = (
  state: IAppState,
  vorvertragsnummer: string,
): Tierart => {
  return (
    selectAlleVorvertraege(state, true)
      .find(vertrag => vertrag.vertragsnummer === vorvertragsnummer)
      ?.vorbelegungdaten?.find(
        vorbelegungsdata => vorbelegungsdata.forApp === App.TKV,
      ) as TkvVorbelegungdaten
  )?.tierart;
};

export const selectTkvV2TierartFromVorvertrag = (
  state: IAppState,
  vorvertragsnummer: string,
): TierartEnum | undefined => {
  const tierart = (
    selectAlleVorvertraege(state, true)
      .find(vertrag => vertrag.vertragsnummer === vorvertragsnummer)
      ?.vorbelegungdaten?.find(
        vorbelegungsdata => vorbelegungsdata.forApp === App.TKV_V2,
      ) as TkvV2Vorbelegungdaten
  )?.tierart;
  switch (tierart) {
    case Tierart.VRHUND:  return TierartEnum.HUND;
    case Tierart.VRKATZE: return TierartEnum.KATZE;
    case Tierart.VRPFERD: return TierartEnum.PFERD;
    default:              return undefined;
  }
};

export const selectEinschliessbareStandaloneProdukte = (
  state: IAppState,
  appID: App,
): Array<StandaloneProdukt> => {
  return state.standaloneProdukte
    .concat(selectFremdStandaloneProdukte(state))
    .filter(
      standaloneProdukt =>
        standaloneProdukt.einschliessenDetails.some(detail => detail.appId === appID) &&
        selectUmstellungsstatusForStandaloneProdukt(state.warenkorb, standaloneProdukt) ===
          Umstellungsstatus.EINSCHLIESSBAR,
    );
};

export const selectStandaloneProdukteForModal = (state: IAppState): Array<StandaloneProdukt> => {
  if (state.transientState.standaloneModalOpenForApp) {
    return selectEinschliessbareStandaloneProdukte(
      state,
      state.transientState.standaloneModalOpenForApp.appId,
    );
  }
  return [];
};

export const selectAlleVorvertraegeForModal = (
  state: IAppState,
  selectedVertrag: Vorvertrag,
  involveWeitereVertraege: boolean,
): Array<Vorvertrag | StandaloneProdukt> => {
  const result: Array<Vorvertrag | StandaloneProdukt> = state.standaloneProdukte
    .concat(selectFremdStandaloneProdukte(state))
    .filter(
      standaloneProdukt =>
        standaloneProdukt.einschliessenDetails.some(detail =>
          selectedVertrag.produktIds.includes(detail.appId),
        ) &&
        selectUmstellungsstatusForStandaloneProdukt(state.warenkorb, standaloneProdukt) ===
          Umstellungsstatus.EINSCHLIESSBAR,
    );
  if (selectedVertrag) {
    return result.concat(
      selectStandardVorvertraegeForModal(state, selectedVertrag, involveWeitereVertraege),
    );
  } else {
    return result;
  }
};

export const selectSomeCheckboxCanBeChecked = (vorvertrag: VorvertragForModal): boolean => {
  return vorvertrag.disabled === false || vorvertrag.ticked === true;
};

export const selectHasRsAndRsVermietungVorvertraege = (state: IAppState, appId: App): boolean => {
  const alleVorvertraege = selectAlleVorvertraege(state, true);
  const isRsAndRsVermietungVorvertraegeExist =
    appId === App.RS &&
    alleVorvertraege.some(
      vorvertrag =>
        vorvertrag.produktIds.includes(App.RS_VERMIETUNG) &&
        !vorvertragIstNichtUmstellbar(vorvertrag),
    );
  const isRsVermietungAndRsVorvertraegeExist =
    appId === App.RS_VERMIETUNG &&
    alleVorvertraege.some(
      vorvertrag =>
        vorvertrag.produktIds.includes(App.RS) &&
        !vorvertragIstNichtUmstellbar(vorvertrag),
    );
  return isRsAndRsVermietungVorvertraegeExist || isRsVermietungAndRsVorvertraegeExist;
};

export const selectHasRsVermietungVorvertragMitBeibehaltung = (state: IAppState): boolean => {
  return selectAlleVorvertraege(state, true).some(vorvertrag => {
    return vorvertrag.produktIds.includes(App.RS_VERMIETUNG) && vorvertrag.beibehaltung;
  });
};

const getRisikoortAdresse = (vorbelegungsdaten: Vorbelegungdaten[]): VorvertragAdresse => {
  if (!vorbelegungsdaten || vorbelegungsdaten.length === 0) {
    return undefined;
  }

  const vwgOrVhvVorbelegung = vorbelegungsdaten.find(vorbelegung =>
    areAppTypesEquivalent(vorbelegung.forApp, App.VWG) || areAppTypesEquivalent(vorbelegung.forApp, App.VHV)
  );

  if (vwgOrVhvVorbelegung) {
    return (vwgOrVhvVorbelegung as unknown as BasisRisikoort).risikoort;
  }

  return undefined;
};

export const selectInitializedVorvertraege = (
  vorvertraege: Array<Vorvertrag>,
  selectedVorvertrag: Vorvertrag,
): VorvertragDataForModal => {
  const selectedStandardVertragsnummern: Array<string> = [];
  const result: Array<VorvertragForModal> = [];

  vorvertraege.forEach(vorvertrag => {
    const auwahlRisikoortAdressen = getRisikoortAdresse(vorvertrag.vorbelegungdaten);

    const vvValid = vorvertrag.risikoortInfo?.valid;
    const gleichesRisiko = isGleichesRisiko(vorvertrag, selectedVorvertrag);

    let ticked;
    let disabled;
    if (selectedVorvertrag.risikoortInfo?.valid) {
      if (!vvValid) {
        ticked = false;
        disabled = false;
      } else if (gleichesRisiko) {
        ticked = true;
        disabled = !vorvertrag.firmenvertrag;
        selectedStandardVertragsnummern.push(vorvertrag.vertragsnummer);
      } else {
        ticked = false;
        disabled = true;
      }
    } else {
      ticked = false;
      disabled = false;
    }
    result.push({
      vorvertrag: vorvertrag,
      ticked: ticked,
      disabled: disabled,
      risikoortadresse: auwahlRisikoortAdressen,
      risikoUnfallVp:
        vorvertrag.produktIds?.[0] === App.UNF
          ? createNameFromVorvertrag(vorvertrag)
          : undefined,
    });
  });
  return {
    selectedStandardVertragsnummern,
    vorvertragForModalArray: result,
  };
};

const isGleichesRisiko = (vv: Vorvertrag, selectedVorvertrag: Vorvertrag): boolean => {
  if (selectedVorvertrag.produktIds[0] === App.UNF) {
    const risikoNameVV = createNameFromVorvertrag(vv);
    const risikoNameSelectedVV = createNameFromVorvertrag(selectedVorvertrag);
    return risikoNameSelectedVV === risikoNameVV;
  } else {
    const selectedRisikoortAdresse = getRisikoortAdresse(selectedVorvertrag.vorbelegungdaten);
    const auwahlRisikoortAdressen = getRisikoortAdresse(vv.vorbelegungdaten);
    return isEqualAdresse(selectedRisikoortAdresse, auwahlRisikoortAdressen);
  }
};

export const selectSelectableAppsForStandaloneEinschliessenModal = (
  state: IAppState,
): Array<App> => {
  const standaloneProdukt = standaloneModalOpenForVertrag(state);
  if (!standaloneProdukt) {
    return [];
  }
  return selectSelectableNeugeschaeftAppsForStandalone(state, standaloneProdukt);
};

export const selectSelectableNeugeschaeftAppsForStandalone = (
  state: IAppState,
  standaloneProdukt: StandaloneProdukt,
): Array<App> => {
  const neugeschaeftSperren: NeugeschaeftSperren[] = selectNeugeschaefteSperren(state);
  return standaloneProdukt.einschliessenDetails
    .filter(detail =>
      neugeschaeftSperren.some(
        sperren => sperren.appId === detail.appId && !sperren.neugeschaeftSperren,
      ),
    )
    .map(detail => detail.appId);
};

export const selectShowGeladeneVorgaengeHinweis = (state: IAppState): boolean => {
  return state.transientState.showGeladeneVorgaengeHinweis;
};

export const selectVertraegeForTable = (
  state: IAppState,
): Array<Vorvertrag | StandaloneProdukt> => {
  const vorvertraege: Array<Vorvertrag> = Array.from(selectVorvertraege(state));
  const standaloneProdukte: Array<StandaloneProdukt> = Array.from(selectStandaloneProdukte(state));
  return createTableOfVorvertraege(standaloneProdukte, vorvertraege);
};

export const selectAppsToDisableForEHV = (state: IAppState): Array<App> => {
  return isAnyStandardForPhVorhanden(state) ? [App.PH] : [];
};

export const selectAppsToCheckForEHV = (state: IAppState): Array<App> => {
  return isAnyPhEhvUmgestellt(state) ? [App.VHV_V2] : [App.PH, App.VHV_V2];
};

export const selectShouldDisableBeibehaltenButton = (state: IAppState): boolean => {
  const tarifierungInProgess = selectTarifierungIsInProgress(state);
  const assekuranztarifChanged = selectAssekuranztarifChanged(state);
  const assekuranztarifMixed = selectAssekuranztarifMixed(state);

  return tarifierungInProgess || assekuranztarifChanged || assekuranztarifMixed;
};

function isAnyStandardForPhVorhanden(state: IAppState): boolean {
  const isStandardForPhVorhanden = selectAlleVorvertraege(state, true).some(
    vorvertrag =>
      vorvertrag?.produktIds.length === 1 &&
      vorvertrag?.produktIds[0] === App.PH &&
      [Umstellungsstatus.UMSTELLBAR, Umstellungsstatus.OPTIONAL_UMSTELLBAR].includes(
        selectUmstellungsstatusForVorvertrag(vorvertrag, state),
      ),
  );

  return isStandardForPhVorhanden || isAnyPhEhvUmgestellt(state);
}

function isAnyPhEhvUmgestellt(state: IAppState): boolean {
  return selectAlleVorvertraege(state, true).some(
    vorvertrag =>
      isEHVVertrag(vorvertrag) &&
      state.warenkorb?.some(
        produkt =>
          produkt.appId === App.PH &&
          produkt.vorvertragsNummern?.some(
            nummer =>
              nummer.spLaufnummer === vorvertrag.spLaufnummer &&
              nummer.vertragsnummer === vorvertrag.vertragsnummer,
          ),
      ) &&
      selectUmstellungsstatusForVorvertrag(vorvertrag, state) === Umstellungsstatus.UMGESTELLT,
  );
}

export function createTableOfVorvertraege(
  standaloneProdukte: Array<StandaloneProdukt>,
  vorvertraege: Array<Vorvertrag>,
): Array<Vorvertrag | StandaloneProdukt> {
  const appIdOrder = [App.PH, App.VHV, App.VHV_V2, App.RS, App.VWG, App.VWG_V2, App.THH_HUND, App.THH_PFERD, App.TKV, App.TKV_V2];
  vorvertraege.sort((a, b) => {
    if (a.produktIds.length > 1) {
      return 1;
    } else if (b.produktIds.length > 1) {
      return -1;
    } else {
      const indexA = appIdOrder.indexOf(a.produktIds[0]);
      const indexB = appIdOrder.indexOf(b.produktIds[0]);
      const rangA = indexA === -1 ? 10000 : indexA;
      const rangB = indexB === -1 ? 10000 : indexB;
      return rangA - rangB;
    }
  });
  standaloneProdukte.sort((a, b) => {
    if (a.einschliessenDetails.length > 1 || b.einschliessenDetails.length === 0) {
      return -1;
    } else if (b.einschliessenDetails.length > 1 || a.einschliessenDetails.length === 0) {
      return 1;
    } else {
      const indexA = appIdOrder.indexOf(a.einschliessenDetails[0].appId);
      const indexB = appIdOrder.indexOf(b.einschliessenDetails[0].appId);
      return indexA - indexB;
    }
  });

  let returnArray: Array<Vorvertrag | StandaloneProdukt> = [];
  returnArray = returnArray.concat(
    vorvertraege.filter(vertrag => vertrag.beibehaltung || vertrag.ersetzen),
  );
  returnArray = returnArray.concat(standaloneProdukte.filter(vertrag => vertrag.ersetzen));
  returnArray = returnArray.concat(
    vorvertraege.filter(
      vertrag => !vertrag.beibehaltung && !vertrag.ersetzen && vertrag.produktIds.length < 2,
    ),
  );
  returnArray = returnArray.concat(standaloneProdukte.filter(vertrag => !vertrag.ersetzen));
  returnArray = returnArray.concat(
    vorvertraege.filter(
      vertrag => !vertrag.beibehaltung && !vertrag.ersetzen && vertrag.produktIds.length > 1,
    ),
  );
  return returnArray;
}

export const selectNeugeschaefteSperren = (state: IAppState): NeugeschaeftSperren[] => {
  return Object.values(App).map(app => {
    return {
      appId: app,
      neugeschaeftSperren: isNeugeschaeftGesperrt(app, state),
    };
  });
};

export const isNeugeschaeftGesperrt = (appId: App, state: IAppState): boolean => {
  if (selectIsNeugeschaeftSperrenDeactivated(state)) {
    return false;
  }
  const geperrterVertrag = selectVorvertraege(state, true).some(
    vorvertrag =>
      vorvertrag.produktIds.includes(appId) &&
      vorvertrag.neugeschaeftSperren.find(
        neugeschaeftSperren => neugeschaeftSperren.appId === appId,
      ).neugeschaeftSperren,
  );
  if (geperrterVertrag) {
    return true;
  }
  return selectFremdVertraege(state)
    .filter(vertrag => !isStandaloneVertrag(vertrag))
    .map(vertrag => vertrag as Vorvertrag)
    .some(
      vorvertrag =>
        vorvertrag.produktIds.includes(appId) &&
        vorvertrag.neugeschaeftSperren.find(
          neugeschaeftSperren => neugeschaeftSperren.appId === appId,
        )?.neugeschaeftSperren,
    );
};

function getUmstellbarkeit(appState: IAppState, vorvertrag: Vorvertrag): Umstellungsstatus {
  const produkte: Array<WarenkorbAngebot> = appState.warenkorb;
  const clonedVorvertrag = cloneDeep(vorvertrag) as Vorvertrag;
  if (
    !clonedVorvertrag.produktIds ||
    clonedVorvertrag.produktIds.every(appId => !Object.values(App).includes(appId))
  ) {
    return Umstellungsstatus.APPID_UNBEKANNT;
  }
  if (vorvertragIstNichtUmstellbar(clonedVorvertrag)) {
    return Umstellungsstatus.NICHT_UMSTELLBAR;
  }
  const expandedProduktIds = new Set(clonedVorvertrag.produktIds);
  clonedVorvertrag.produktIds?.forEach(produktId => {
    getEquivalentApps(produktId).forEach(equiv => expandedProduktIds.add(equiv));
  });
  clonedVorvertrag.produktIds = Array.from(expandedProduktIds);

  if (produkte) {
    const foundWarenkorbAngeboteForProduktId: WarenkorbAngebot[] = produkte
      .filter(produkt => clonedVorvertrag.produktIds?.includes(produkt.appId))
      .concat(
        findRsMitUmgestellterRsVermietungImWarenkorb(
          clonedVorvertrag.produktIds?.[0],
          produkte,
          clonedVorvertrag,
        ),
      );

    if (foundWarenkorbAngeboteForProduktId.length === 0) {
      return clonedVorvertrag.umstellungszwang
        ? Umstellungsstatus.UMSTELLBAR
        : Umstellungsstatus.OPTIONAL_UMSTELLBAR;
    }

    const foundVorvertragImAngebot: WarenkorbAngebot = foundWarenkorbAngeboteForProduktId.find(
      warenkorbAngebot =>
        warenkorbAngebot.vorvertragsNummern?.some(
          feVorvertragsNummer =>
            feVorvertragsNummer.vertragsnummer === clonedVorvertrag.vertragsnummer &&
            feVorvertragsNummer.spLaufnummer === clonedVorvertrag.spLaufnummer,
        ),
    );
    const isMPF = clonedVorvertrag.produktIds?.some(produktId => isMultistoreApp(produktId));

    if (foundVorvertragImAngebot) {
      if (foundVorvertragImAngebot.beibehalten) {
        return Umstellungsstatus.BEIBEHALTEN;
      }
      return Umstellungsstatus.UMGESTELLT;
    }
    if (isMPF) {
      return clonedVorvertrag.umstellungszwang
        ? Umstellungsstatus.UMSTELLBAR
        : Umstellungsstatus.OPTIONAL_UMSTELLBAR;
    } else {
      return Umstellungsstatus.UMSTELLBAR_ABER_PRODUKT_BEREITS_SELECTED;
    }
  }
  return clonedVorvertrag.umstellungszwang
    ? Umstellungsstatus.UMSTELLBAR
    : Umstellungsstatus.OPTIONAL_UMSTELLBAR;
}

export function findRsMitUmgestellterRsVermietungImWarenkorb(
  appId: App,
  produkte: WarenkorbAngebot[],
  vorvertrag: Vorvertrag,
): WarenkorbAngebot[] {
  if (appId !== App.RS_VERMIETUNG) {
    return [];
  }
  const rsMitUmgestellterRsVermietungImWarenkorb = produkte.find(
    produkt =>
      produkt.appId === App.RS &&
      produkt.vorvertragsNummern?.some(
        feVorvertragsnummer =>
          feVorvertragsnummer.vertragsnummer === vorvertrag.vertragsnummer &&
          feVorvertragsnummer.spLaufnummer === vorvertrag.spLaufnummer,
      ),
  );
  return rsMitUmgestellterRsVermietungImWarenkorb ? [rsMitUmgestellterRsVermietungImWarenkorb] : [];
}

export const selectUmstellungsstatus = (
  vertrag: Vorvertrag | StandaloneProdukt,
  appState: IAppState,
): Umstellungsstatus => {
  return isStandaloneVertrag(vertrag)
    ? selectUmstellungsstatusForStandaloneProdukt(appState.warenkorb, vertrag)
    : selectUmstellungsstatusForVorvertrag(vertrag, appState);
};

export function setVertragStatusOptionalForAuslaendischePostlandschluessel(
  vertragStatus: Umstellungsstatus,
  vorvertrag: Vorvertrag,
): Umstellungsstatus {
  if (vertragStatus === Umstellungsstatus.UMSTELLBAR) {
    if (
      vorvertrag.vorbelegungdaten?.some(vorbelegungdaten => {
        if (!areAppTypesEquivalent(vorbelegungdaten.forApp, App.VWG) && !areAppTypesEquivalent(vorbelegungdaten.forApp, App.VHV)) {
          return false;
        }
        const vwgVhvVorbelegungdaten = vorbelegungdaten as
          | VwgVorbelegungdaten
          | VhvVorbelegungdaten;
        if (vwgVhvVorbelegungdaten) {
          return (
            vwgVhvVorbelegungdaten.risikoort?.postlandSchluessel &&
            vwgVhvVorbelegungdaten.risikoort?.postlandSchluessel !== 'DE'
          );
        } else {
          return false;
        }
      })
    ) {
      vertragStatus = Umstellungsstatus.OPTIONAL_UMSTELLBAR;
    }
  }
  return vertragStatus;
}

export const selectUmstellungsstatusForVorvertrag = (
  vorvertrag: Vorvertrag,
  appState: IAppState,
): Umstellungsstatus => {
  let vertragStatus = getUmstellbarkeit(appState, vorvertrag);
  vertragStatus = setVertragStatusOptionalForAuslaendischePostlandschluessel(
    vertragStatus,
    vorvertrag,
  );
  if (
    [Umstellungsstatus.UMSTELLBAR, Umstellungsstatus.OPTIONAL_UMSTELLBAR].includes(vertragStatus) &&
    vorvertrag.beibehaltung
  ) {
    vertragStatus = Umstellungsstatus.AKTUELL;
  }
  return vertragStatus;
};

export const selectUmstellungsstatusForStandaloneProdukt = (
  warenkorbAngebote: Array<WarenkorbAngebot>,
  standaloneProdukt: StandaloneProdukt,
): Umstellungsstatus => {
  if (standaloneProdukt?.ersetzen !== true || standaloneProdukt.einschliessenDetails.length === 0) {
    return Umstellungsstatus.NICHT_UMSTELLBAR;
  }

  const umgestellt = warenkorbAngebote.some(produkt =>
    produkt.vorvertragsNummern?.some(
      vorvertragsNr =>
        vorvertragsNr.vertragsnummer === standaloneProdukt.vertragsnummer &&
        vorvertragsNr.spLaufnummer === standaloneProdukt.spLaufnummer,
    ),
  );

  if (umgestellt) {
    return Umstellungsstatus.EINGESCHLOSSEN;
  } else {
    return Umstellungsstatus.EINSCHLIESSBAR;
  }
};

export const selectSelectedErsatzProduktForVorvertrag = (
  state: IAppState,
  vorvertragsnummer: string,
  spLaufnummer: number,
): WarenkorbAngebot => {
  return state.warenkorb.find(
    produkt =>
      produkt.vorvertragsNummern?.some(
        vorvertragNr =>
          vorvertragNr.vertragsnummer === vorvertragsnummer &&
          vorvertragNr.spLaufnummer === spLaufnummer,
      ),
  );
};

export const selectVorvertragByVertragsnummerAndSpLaufnummer = (
  state: IAppState,
  vorvertragsnummer: string,
  spLaufnummer: number,
): Vertrag | null => {
  const alleVertrage = [].concat(
    selectVorvertraege(state),
    selectStandaloneProdukte(state),
    selectFremdVertraege(state),
  );
  return findVertragByVertragsnummerAndSpLaufnummer(alleVertrage, vorvertragsnummer, spLaufnummer);
};

export function findVertragByVertragsnummerAndSpLaufnummer(
  vertraege: Vertrag[],
  vorvertragsnummer: string,
  spLaufnummer: number,
): Vertrag | null {
  if (vertraege) {
    return vertraege.find(
      vertrag =>
        vertrag.vertragsnummer === vorvertragsnummer && vertrag.spLaufnummer === spLaufnummer,
    );
  }
  return null;
}

export function isStandaloneVertrag(
  vertrag: Vorvertrag | StandaloneProdukt,
): vertrag is StandaloneProdukt {
  return vertrag && (vertrag as StandaloneProdukt).einschliessenDetails != null;
}

export function isVorvertrag(vertrag: Vertrag): vertrag is Vorvertrag {
  return vertrag && (vertrag as StandaloneProdukt).einschliessenDetails == null;
}

export function isEHVVertrag(vertrag: Vorvertrag): boolean {
  return (
    vertrag?.produktIds.filter(id => areAppTypesEquivalent(id, App.VHV) || id === App.PH).length === 2
  );
}

export function getVertragAppIds(vertrag: StandaloneProdukt | Vorvertrag): App[] {
  return isVorvertrag(vertrag)
    ? vertrag.produktIds
    : vertrag.einschliessenDetails.map(detail => detail.appId);
}

export const selectFremdStandaloneProdukte = (state: IAppState): Array<StandaloneProdukt> => {
  return state.fremdVertraege
    ?.filter(vertrag => isStandaloneVertrag(vertrag))
    .map(vertrag => vertrag as StandaloneProdukt);
};

export const selectSelectableFremdStandaloneProdukte = (
  state: IAppState,
): Array<StandaloneProdukt> => {
  return selectFremdStandaloneProdukte(state).filter(
    standaloneProdukt =>
      selectUmstellungsstatusForStandaloneProdukt(state.warenkorb, standaloneProdukt) ===
      Umstellungsstatus.EINSCHLIESSBAR,
  );
};

export const selectFremdStandardProdukte = (state: IAppState): Array<Vorvertrag> => {
  return state.fremdVertraege
    ?.filter(vertrag => !isStandaloneVertrag(vertrag))
    .map(vertrag => vertrag as Vorvertrag);
};

export const selectInvalidWarenkorbProdukte = (
  validatedInvalidProdukte: WarenkorbValidationResult[],
  state: IAppState,
): WarenkorbValidationResult[] => {
  if (
    !validatedInvalidProdukte ||
    validatedInvalidProdukte.length === 0 ||
    !selectIsNeugeschaeftSperrenDeactivated(state)
  ) {
    return validatedInvalidProdukte;
  }
  return validatedInvalidProdukte?.filter(
    produkt => produkt.validationError !== WarenkorbProduktError.NEUGESCHAEFT_GESPERRT,
  );
};

export const selectStandardVorvertraegeForModal = (
  state: IAppState,
  selectedVorvertrag: Vorvertrag,
  involveWeitereVertraege: boolean,
  rsAndRsVermietung: RsAndRsVermietung = null,
): Array<Vorvertrag> => {
  // keine Multiprodukte haben VWG, deswegen 0-te Element
  if (
    !selectedVorvertrag ||
    !areAppTypesEquivalent(selectedVorvertrag.produktIds[0], App.VWG) ||
    !isMultistoreApp(selectedVorvertrag.produktIds[0])
  ) {
    if (![App.RS, App.RS_VERMIETUNG, App.UNF].includes(selectedVorvertrag?.produktIds[0])) {
      return [];
    }
  }

  const appId = selectedVorvertrag.produktIds[0];
  const multiProduktId: App = selectedVorvertrag.produktIds.find(produktId =>
    isMultistoreApp(produktId),
  );
  const umstellungMoeglich = new Set([
    Umstellungsstatus.UMSTELLBAR,
    Umstellungsstatus.OPTIONAL_UMSTELLBAR,
    Umstellungsstatus.AKTUELL,
  ]);

  const fremdVorvertraege: Array<Vorvertrag> = selectFremdVertraege(state)
    ? selectFremdVertraege(state)
        .filter(fremdVorvertrag => !isStandaloneVertrag(fremdVorvertrag))
        .map(fremdVorvertrag => fremdVorvertrag as Vorvertrag)
    : [];
  const vorvertraege = selectVorvertraege(state, involveWeitereVertraege)
    .concat(fremdVorvertraege)
    .filter(
      vorvertrag => {
        const sparteMatches = filterVorvertragSparte(vorvertrag, appId, multiProduktId);
        const rsCheck = !isRsVermietungAndHasBeibehaltung(vorvertrag, rsAndRsVermietung);
        const umstellungsstatus = selectUmstellungsstatusForVorvertrag(vorvertrag, state);
        const statusOk = umstellungMoeglich.has(umstellungsstatus);
        const notSameContract = vorvertrag.vertragsnummer !== selectedVorvertrag.vertragsnummer ||
                                vorvertrag.spLaufnummer !== selectedVorvertrag.spLaufnummer;

        return sparteMatches && rsCheck && statusOk && notSameContract;
      }
    );

  return filterUnfallVorvertraege(selectedVorvertrag, vorvertraege);
};

const filterVorvertragSparte = (
  vorvertrag: Vorvertrag,
  appId: App,
  multiProduktId: App,
): boolean => {
  return vorvertrag.produktIds.some(
    produktId =>
      areAppTypesEquivalent(produktId, multiProduktId) ||
      (appId === App.RS && (produktId === App.RS || produktId === App.RS_VERMIETUNG)) ||
      (appId === App.RS_VERMIETUNG && (produktId === App.RS || produktId === App.RS_VERMIETUNG)),
  );
};

const isRsVermietungAndHasBeibehaltung = (
  vorvertrag: Vorvertrag,
  rsAndRsVermietung: RsAndRsVermietung,
): boolean => {
  return (
    !rsAndRsVermietung?.shouldCombine &&
    vorvertrag.produktIds.includes(App.RS_VERMIETUNG) &&
    vorvertrag.beibehaltung
  );
};

const filterUnfallVorvertraege = (
  selectedVorvertrag: Vorvertrag,
  vorvertraege: Vorvertrag[],
): Vorvertrag[] => {
  const selectedVvValid = selectedVorvertrag.risikoortInfo?.valid;
  const andereVvValid = vorvertraege.every(vv => vv.risikoortInfo?.valid);

  if (selectedVvValid && andereVvValid) {
    return vorvertraege.filter(vv => {
      if (vv.produktIds[0] === App.UNF) {
        return createNameFromVorvertrag(selectedVorvertrag) === createNameFromVorvertrag(vv);
      }
      return vv;
    });
  }
  return vorvertraege;
};

export const createNameFromVorvertrag = (vv: Vorvertrag): string => {
  if (vv?.vorbelegungdaten?.length > 0) {
    const { vorname, nachname, geburtsdatum } = vv.vorbelegungdaten[0];
    return `${vorname}${nachname}${geburtsdatum}`;
  }
  return '';
};

export const hasUmstellungszwangOrBeibehalten = (state: IAppState): boolean => {
  const vorvertraege = selectVorvertraege(state);

  return vorvertraege.some(vorvertrag => {
    const umstellungsstatus = selectUmstellungsstatus(vorvertrag, state);
    return [Umstellungsstatus.AKTUELL, Umstellungsstatus.UMSTELLBAR].includes(umstellungsstatus);
  });
};

export const hasPersonVorvertraege = (
  vorvertraege: Vertrag[],
  person: { vorname: string; nachname: string; geburtsdatum: string },
): boolean => {
  return (
    (person &&
      vorvertraege
        .map((vorvertrag: Vertrag) => vorvertrag?.risikoortInfo?.anzeige)
        .includes(getPrintedVpDaten(VP_PREFIX, person))) ||
    false
  );
};

export const selectVorvertraegeForVP = (
  risikoortAnzeige: string,
  vorvertraege: Vorvertrag[] = [],
): Array<Vorvertrag> => {
  return vorvertraege.filter(vorvertrag => vorvertrag?.risikoortInfo?.anzeige === risikoortAnzeige);
};

function vorvertragIstNichtUmstellbar(vorvertrag: Vorvertrag): boolean {
  return Boolean(
    (!vorvertrag.ersetzen && !vorvertrag.beibehaltung) ||
      (vorvertrag.isStorniert && vorvertrag.stornoDetail),
  );
}
